import React, { Component } from 'react';
import { t, dateLocale } from '../../i18n';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import '../../style/DisplayManagement.scss';
import '../../style/DisplaysList.scss';
import { Query } from 'react-apollo';
import queries from '../../queries';
import { FormattedMessage, injectIntl } from 'react-intl';
import NeedAdmin from '../NeedAdmin';

// En-tête du tableau (masqué en mobile via CSS). `onSort` reçoit le nom de colonne.
function DisplayListHeader({ onSort }) {
  return (
    <li className='tsk-displays-row'>
      <div className={"tsk-displays-list-item"}>
        <div className='tsk-display-col-item bold'>Row</div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('name')}><FormattedMessage id="displays.list.colone.name" /></div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('location')}><FormattedMessage id="displays.list.colone.location" /></div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('project')}><FormattedMessage id="displays.list.colone.current" /></div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('connected')}><FormattedMessage id="displays.list.colone.state" /></div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('physical_id')}><FormattedMessage id="displays.list.colone.physical_id" /></div>
        <div className='tsk-display-col-item bold cursor-pointor' onClick={() => onSort('last_time_connected')}><FormattedMessage id="displays.list.colone.last_connected" /></div>
        <div className='tsk-display-col-item bold'><FormattedMessage id="displays.list.colone.version" /></div>
        <div className='tsk-display-col-item bold'>Device group</div>
      </div>
    </li>
  );
}

// Ce que la borne a réellement installé (rapporté par elle-même après téléchargement), avec repli sur le
// numéro de version seul si le projet a depuis été supprimé, ou "None" si la borne n'a jamais publié/vidé
// son cache. Distinct de `device.project` (l'assignation en base, qui peut être différente ou périmée).
function installedLabel(device) {
  const install = device.currentInstall;
  if (!install || install.version == null) return 'None';
  const projectName = install.project && install.project.name;
  return projectName ? `${projectName} (v${install.version})` : `v${install.version}`;
}

// Date/heure de dernière connexion : "En ligne" si connectée, "—" si inconnue ou date invalide.
function lastConnectedLabel(device, onlineLabel) {
  if (device.connected) return onlineLabel;
  if (!device.last_time_connected) return '—';
  const date = new Date(device.last_time_connected);
  if (isNaN(date.getTime())) return '—';
  try {
    return date.toLocaleString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return date.toISOString();
  }
}

// Une ligne appareil. `data-label` (traduit) sert d'étiquette en mode carte (mobile).
function DisplayRow({ device, index, intl }) {
  const t = (id, fallback) => {
    try { return intl.formatMessage({ id }); } catch (e) { return fallback; }
  };
  return (
    <li className='tsk-displays-row' key={device.id}>
      <Link to={'/admin/displays/' + device.id} className={'tsk-displays-list-item'}>
        <div className='tsk-display-col-item tsk-col-hide-mobile' data-label="#">{index + 1}</div>
        <div className='tsk-display-col-item' data-label={t('displays.list.colone.name', 'Device name')}>{(device.name == null) ? 'No name' : device.name}</div>
        <div className='tsk-display-col-item' data-label={t('displays.list.colone.location', 'Location')}>{device.location == null ? 'No location' : device.location}</div>
        <div className='tsk-display-col-item' data-label={t('displays.list.colone.current', 'Project')}>{(device.project == null) ? 'No project' : device.project ? device.project.name : 'No project'}</div>
        <div className={'tsk-display-col-item tsk-col-connected ' + (device.connected ? 'is-connected' : 'is-offline')} data-label={t('displays.list.colone.state', 'Connected')}>{device.connected ? "Yes" : "No"}</div>
        <div className='tsk-display-col-item tsk-col-hide-mobile' data-label={t('displays.list.colone.physical_id', 'Physical ID')}>{device.physical_id}</div>
        <div className='tsk-display-col-item' data-label={t('displays.list.colone.last_connected', 'Last connection')}>{lastConnectedLabel(device, t('displays.list.last_connected.online', 'Online'))}</div>
        <div className='tsk-display-col-item' data-label={t('displays.list.colone.version', 'Version')}>{installedLabel(device)}</div>
        <div className='tsk-display-col-item tsk-col-hide-mobile' data-label="Device group">{(device.devicesGroup) ? device.devicesGroup.name : 'No group'}</div>
      </Link>
    </li>
  );
}

class DisplaysList extends Component {

    constructor(props) {
        super(props);
        this.state = {
          sortColumn: 'name',
          sortOrder: 'asc', // 'asc' for ascending, 'desc' for descending
          search: "",
          connectedFilter: 'all', // 'all' | 'connected' | 'offline'
          group_id: null,
        };
      }

      handleSort = (column) => {
        const { sortColumn, sortOrder } = this.state;

        if (sortColumn === column) {
          // Si vous cliquez sur la même colonne, basculez l'ordre de tri
          this.setState({
            sortOrder: sortOrder === 'asc' ? 'desc' : 'asc',
          });
        } else {
          // Si vous cliquez sur une nouvelle colonne, triez par ordre alphabétique ascendant par défaut
          this.setState({
            sortColumn: column,
            sortOrder: 'asc',
          });
        }
      };

      // Tri partagé par les trois variantes de route.
      sortDevices = (devices) => {
        const { sortOrder, sortColumn } = this.state;
        return devices.slice().sort((a, b) => {
          const aValue = a[sortColumn];
          const bValue = b[sortColumn];

          if (sortColumn === 'project') {
            if (aValue !== null && bValue !== null) {
              const aProjectName = a.project && a.project.name ? a.project.name : '';
              const bProjectName = b.project && b.project.name ? b.project.name : '';
              return sortOrder === 'asc' ? aProjectName.localeCompare(bProjectName) : bProjectName.localeCompare(aProjectName);
            }
            return 0;
          }
          if (sortColumn === 'name' || sortColumn === 'physical_id') {
            const aProjectName = aValue || 'no name';
            const bProjectName = bValue || 'no name';
            return sortOrder === 'asc' ? aProjectName.localeCompare(bProjectName) : bProjectName.localeCompare(aProjectName);
          }
          if (sortColumn === 'last_time_connected') {
            // En ligne = le plus récent ; inconnu = le plus ancien
            const time = (d) => d.connected ? Number.MAX_SAFE_INTEGER : (d.last_time_connected ? (new Date(d.last_time_connected).getTime() || 0) : 0);
            return sortOrder === 'asc' ? time(a) - time(b) : time(b) - time(a);
          }
          if (sortColumn === 'connected') {
            const aProjectName = aValue ? 'yes' : 'no';
            const bProjectName = bValue ? 'yes' : 'no';
            return sortOrder === 'asc' ? aProjectName.localeCompare(bProjectName) : bProjectName.localeCompare(aProjectName);
          }
          return false;
        });
      };

      // Filtre recherche (toutes colonnes, insensible à la casse).
      matchesSearch = (device) => {
        const lowerSearch = this.state.search.toLowerCase();
        return (
          (device.name && device.name.toLowerCase().includes(lowerSearch)) ||
          (device.project && device.project.name && device.project.name.toLowerCase().includes(lowerSearch)) ||
          (device.physical_id && device.physical_id.toLowerCase().includes(lowerSearch)) ||
          installedLabel(device).toLowerCase().includes(lowerSearch) ||
          (device.connected ? "Yes" : "No").toLowerCase().includes(lowerSearch) ||
          (device.location && device.location.toLowerCase().includes(lowerSearch))
        );
      };

      // Filtre statut de connexion (tous / connectés / hors-ligne).
      matchesConnectedFilter = (device) => {
        const { connectedFilter } = this.state;
        if (connectedFilter === 'connected') return !!device.connected;
        if (connectedFilter === 'offline') return !device.connected;
        return true;
      };

      // Coquille commune : barre d'outils + en-tête + lignes rendues par renderRows().
      renderShell(renderRows) {
        return (
          <NeedAdmin trueAdmin>
            <div className="tsk-displays-page">
              <div className="tsk-displays-toolbar">
                <button type="button" className='tsk-button tsk-displays-toolbar__back' onClick={() => { window.history.back() }}>Retour</button>
                <input className='tsk-input-search' type='text' placeholder={t('ui.search_ph')} value={this.state.search} onChange={(e) => { this.setState({ search: e.target.value }) }} />
                <select
                  className='tsk-displays-connected-filter'
                  value={this.state.connectedFilter}
                  onChange={(e) => { this.setState({ connectedFilter: e.target.value }) }}
                >
                  <option value='all'>Tous les appareils</option>
                  <option value='connected'>Connectés</option>
                  <option value='offline'>Hors-ligne</option>
                </select>
              </div>
              <ul className='tsk-displays-list-container'>
                <DisplayListHeader onSort={this.handleSort} />
                {renderRows()}
              </ul>
            </div>
          </NeedAdmin>
        );
      }

      // Liste -> lignes filtrées + triées. `nameMustBeNull` est la particularité de la route 'noname'.
      renderRowsFor(devices, nameMustBeNull = false) {
        const { intl } = this.props;
        return this.sortDevices(devices).map((device, index) => {
          if (nameMustBeNull && device.name) return null;
          if (!this.matchesConnectedFilter(device)) return null;
          if (!this.matchesSearch(device)) return null;
          return <DisplayRow key={device.id} device={device} index={index} intl={intl} />;
        });
      }

      render() {
        const { groupId } = this.props.match.params;

        if (groupId === 'noname') {
          return this.renderShell(() => (
            <Query query={queries.displays.list} variables={{ groupId: 'all' }}>
              {({ loading, error, data }) => {
                if (loading) return 'loading';
                if (error) return error.message;
                return this.renderRowsFor(data.devices, true);
              }}
            </Query>
          ));
        }

        if (groupId !== 'all') {
          return this.renderShell(() => (
            <Query query={queries.displays.listByGroup} variables={{ id: groupId }}>
              {({ loading, error, data }) => {
                if (loading) return 'loading';
                if (error) return error.message;
                return this.renderRowsFor(data.devicesGroup.devices);
              }}
            </Query>
          ));
        }

        return this.renderShell(() => (
          <Query query={queries.displays.list} variables={{ id: groupId }}>
            {({ loading, error, data }) => {
              if (loading) return 'loading';
              if (error) return error.message;
              return this.renderRowsFor(data.devices);
            }}
          </Query>
        ));
      }
    }

export default injectIntl(DisplaysList);
